import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import useAdminStore from '../stores/useAdminStore';
import LineChart from '../components/LineChart';
import BarChart from '../components/BarChart';
import exportToPDF from '../utils/exportToPDF';
import useAuthStore from '../stores/useAuthStore';

const AdminAnalyticsPage = () => {
  const { user } = useAuthStore();
  const {
    platformAnalytics,
    growthData,
    systemHealth,
    fetchPlatformAnalytics,
    fetchGrowthData,
    fetchSystemHealth,
    isLoading,
    error
  } = useAdminStore();

  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    endDate: new Date()
  });

  useEffect(() => {
    fetchPlatformAnalytics();
    fetchSystemHealth();
    fetchGrowthData(dateRange.startDate, dateRange.endDate);
  }, [dateRange, fetchGrowthData, fetchPlatformAnalytics, fetchSystemHealth]);

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: new Date(value)
    }));
  };

  const resetDateRange = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateRange({ startDate: thirtyDaysAgo, endDate: now });
  };

  const exportAdminReport = () => {
    if (!platformAnalytics || !growthData) return;

    const columns = ['Metric', 'Value'];
    const rows = [
      ['Total Users', platformAnalytics.totalUsers.toString()],
      ['Active Users', platformAnalytics.activeUsers.toString()],
      ['Total Courses', platformAnalytics.totalCourses.toString()],
      ['Approved Courses', platformAnalytics.approvedCourses.toString()],
      ['Total Revenue', `₦${platformAnalytics.totalRevenue.toLocaleString()}`],
      ['Active Sessions', platformAnalytics.activeSessions.toString()]
    ];

    exportToPDF(platformAnalytics, 'Admin_Analytics_Report', columns, rows);
  };

  if (isLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading analytics data</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
            <button
              onClick={() => {
                fetchPlatformAnalytics();
                fetchGrowthData(dateRange.startDate, dateRange.endDate);
                fetchSystemHealth();
              }}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Admin Analytics</h1>
              <p className="text-neutral-600 dark:text-neutral-400">Monitor platform health, user growth, and system performance</p>
            </div>
            <button
              onClick={exportAdminReport}
              className="px-4 py-2 bg-success-500 hover:bg-success-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-success-500"
            >
              Export Report
            </button>
          </div>

          {/* Date Range Picker */}
          <div className="mb-8 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Select Date Range</h3>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate.toISOString().split('T')[0]}
                  onChange={handleDateRangeChange}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate.toISOString().split('T')[0]}
                  onChange={handleDateRangeChange}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={resetDateRange}
                  className="px-4 py-2 mt-6 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                >
                  Reset to 30 Days
                </button>
              </div>
            </div>
          </div>

          {/* KPI Stats */}
          {platformAnalytics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 mb-8">
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">👥</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Users</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {platformAnalytics.totalUsers}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">👥</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Active Users</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {platformAnalytics.activeUsers}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">🎓</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Courses</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {platformAnalytics.totalCourses.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">📈</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Revenue</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      ₦{platformAnalytics.totalRevenue.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">💰</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Account Balance</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      ₦{platformAnalytics.paystackBalance.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">📊</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Active Sessions</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {platformAnalytics.activeSessions}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">⏳</span>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Approved Courses</p>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {platformAnalytics.approvedCourses}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Growth Charts */}
          {growthData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* User Growth */}
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">User Growth</h2>
                <LineChart
                  data={{
                    labels: growthData.userGrowth.map(item => item._id),
                    datasets: [{
                      label: 'New Users',
                      data: growthData.userGrowth.map(item => item.count),
                      borderColor: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      fill: true
                    }]
                  }}
                  height={300}
                />
              </div>

              {/* Course Growth */}
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Course Growth</h2>
                <BarChart
                  data={{
                    labels: growthData?.courseGrowth.map(item => item?._id),
                    datasets: [{
                      label: 'New Courses',
                      data: growthData?.courseGrowth.map(item => item?.count),
                      backgroundColor: '#10b981'
                    }]
                  }}
                  height={300}
                />
              </div>
            </div>
          )}

          {/* System Health */}
          {systemHealth && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">System Health</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">CPU Usage</p>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-2">{systemHealth.cpu.usage.toFixed(1)}%</p>
                  <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mt-2">
                    <div
                      className={`h-2 rounded-full ${systemHealth.cpu.usage > 80 ? 'bg-red-500' : systemHealth.cpu.usage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                      style={{ width: `${systemHealth.cpu.usage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Memory Usage</p>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-2">{systemHealth.memory.usage.toFixed(1)}%</p>
                  <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mt-2">
                    <div
                      className={`h-2 rounded-full ${systemHealth.memory.usage > 85 ? 'bg-red-500' : systemHealth.memory.usage > 70 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                      style={{ width: `${systemHealth.memory.usage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Storage Usage</p>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-2">{systemHealth.storage.used.toFixed(1)}%</p>
                  <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mt-2">
                    <div
                      className={`h-2 rounded-full ${systemHealth.storage.used > 90 ? 'bg-red-500' : systemHealth.storage.used > 75 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                      style={{ width: `${systemHealth.storage.used}%` }}
                    ></div>
                  </div>
                </div>
                <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">System Status</p>
                  <p className={`text-2xl font-bold mt-2 ${systemHealth.status === 'healthy' ? 'text-green-500' :
                      systemHealth.status === 'warning' ? 'text-yellow-500' : 'text-red-500'
                    }`}>
                    {systemHealth.status.charAt(0).toUpperCase() + systemHealth.status.slice(1)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminAnalyticsPage;